import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

interface Options {
  /** Milliseconds of quiet before saving. */
  delay?: number;
  enabled?: boolean;
}

/**
 * Saves `value` a short while after it stops changing.
 *
 * `save` must be referentially stable (wrap it in useCallback) — an unstable reference
 * re-arms the timer on every render and the save never fires.
 *
 * Guarantee: at most `delay` worth of edits is lost if the tab is killed outright, since a
 * request cannot reliably be issued from beforeunload. Everything else is covered by
 * flush().
 */
export function useDebouncedAutosave<T>(
  value: T,
  save: (value: T) => Promise<unknown>,
  { delay = 1000, enabled = true }: Options = {},
) {
  const [state, setState] = useState<AutosaveState>('idle');
  const latest = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRun = useRef(true);
  const isPending = useRef(false);

  latest.current = value;

  const run = useCallback(async () => {
    isPending.current = false;
    setState('saving');
    try {
      await save(latest.current);
      setState('saved');
    } catch {
      setState('error');
    }
  }, [save]);

  useEffect(() => {
    if (!enabled) return;

    // Skip the first pass so freshly loaded data is not written straight back.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (timer.current) clearTimeout(timer.current);
    isPending.current = true;
    timer.current = setTimeout(run, delay);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, enabled, delay, run]);

  /** Saves immediately if anything is queued. Await before navigating away. */
  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!isPending.current) return;
    await run();
  }, [run]);

  // Warn on tab close while a save is still outstanding.
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!isPending.current && state !== 'saving') return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state]);

  return { state, flush };
}
